// Returns "/user" for user-scoped tokens, "" for company-scoped tokens.
// The API serves user-scoped routes under the /user prefix.
function scopePrefix() {
  return sessionStorage.getItem("field_token_scope") === "user" ? "/user" : "";
}

// Returns "?company_uuid=<uuid>" for user-scoped tokens, "" for company-scoped.
// Allows the API to enforce permissions against the correct company.
function companyUuidParam() {
  const uuid = sessionStorage.getItem("field_company_uuid");
  return uuid ? `?company_uuid=${uuid}` : "";
}

export default {
  // ============================================
  // ROLES API
  // ============================================

  getRoles: async function () {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}${scopePrefix()}/role`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  createRole: async function (roleData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = targetCompanyUuid
      ? `?company_uuid=${targetCompanyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/role${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(roleData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  updateRole: async function (uuid, roleData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = targetCompanyUuid
      ? `?company_uuid=${targetCompanyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/role/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(roleData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  deleteRole: async function (uuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = targetCompanyUuid
      ? `?company_uuid=${targetCompanyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/role/${uuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  // ============================================
  // USERS API
  // ============================================

  getUsers: async function (companyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const param = companyUuid
      ? `?company_uuid=${companyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user${param}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  getUser: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}${companyUuidParam()}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  createUser: async function (userData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = targetCompanyUuid
      ? `?company_uuid=${targetCompanyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(userData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  updateUser: async function (uuid, userData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = targetCompanyUuid
      ? `?company_uuid=${targetCompanyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(userData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  deleteUser: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}${companyUuidParam()}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  inviteUser: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}/invite${companyUuidParam()}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  bulkUpdateUsers: async function (userUpdates) {
    const auth_token = await sessionStorage.getItem("token");

    // Execute all updates in parallel
    const promises = userUpdates.map((update) => {
      const request = new Request(
        `${process.env.REACT_APP_API_BASE_URL}${scopePrefix()}/company-user/${
          update.uuid
        }${companyUuidParam()}`,
        {
          method: "PUT",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: auth_token,
          }),
          body: JSON.stringify(update.data),
        }
      );

      return fetch(request)
        .then(async (response) => {
          const json = await response.json();
          if (!response.ok) {
            return {
              uuid: update.uuid,
              status: response.status,
              success: false,
              data: json,
            };
          }
          return {
            uuid: update.uuid,
            status: json.status || response.status,
            success: true,
            data: json.data || json,
          };
        })
        .catch(() => ({
          uuid: update.uuid,
          status: 0,
          success: false,
          data: null,
        }));
    });

    try {
      const results = await Promise.all(promises);
      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      return {
        status: failed === 0 ? 200 : 207, // 207 = Multi-Status
        data: {
          successful,
          failed,
          results,
        },
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  bulkInviteUsers: async function (userUuids) {
    const auth_token = await sessionStorage.getItem("token");

    // Execute all invites in parallel
    const promises = userUuids.map((uuid) => {
      const request = new Request(
        `${
          process.env.REACT_APP_API_BASE_URL
        }${scopePrefix()}/company-user/${uuid}/invite${companyUuidParam()}`,
        {
          method: "PUT",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: auth_token,
          }),
        }
      );

      return fetch(request)
        .then((response) => {
          if (!response.ok) {
            return {
              uuid,
              status: response.status,
              success: false,
              data: null,
            };
          }
          return response.json().then((json) => ({
            uuid,
            status: json.status || response.status,
            success: true,
            data: json.data || json,
          }));
        })
        .catch(() => ({
          uuid,
          status: 0,
          success: false,
          data: null,
        }));
    });

    try {
      const results = await Promise.all(promises);
      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      return {
        status: failed === 0 ? 200 : 207, // 207 = Multi-Status
        data: {
          successful,
          failed,
          results,
        },
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  // ============================================
  // LICENSES API
  // ============================================

  getLicenses: async function () {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/license/availability${companyUuidParam()}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  getCompanies: async function () {
    const auth_token = sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/authorization`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || null,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getMyPermissions: async function () {
    const auth_token = sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/user/session`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      return {
        status: json.status || response.status,
        data: json.data || null,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getGroups: async function (targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = targetCompanyUuid
      ? `?company_uuid=${targetCompanyUuid}`
      : companyUuidParam();
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group${companyParam}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },
};
